/**
 * videoCompressionService.ts
 *
 * Client-side video compression using Canvas + MediaRecorder.
 * Reduces resolution to 720p max and controls bitrate for smaller uploads.
 * Audio is preserved via captureStream() or AudioContext fallback.
 *
 * Flow: load video → draw scaled frames to canvas → re-record at target bitrate
 * Runs at 1x playback speed to preserve audio fidelity.
 */

// ─── Compression targets ──────────────────────────────────────

const MAX_DIMENSION = 720;                         // 720p max (either axis)
const VIDEO_BITRATE = 1_500_000;                   // 1.5 Mbps
const SKIP_THRESHOLD = 5 * 1024 * 1024;            // Files under 5 MB → skip entirely
const SKIP_LOWRES_THRESHOLD = 15 * 1024 * 1024;    // Files ≤720p AND under 15 MB → skip
                                                    // (live captures at 1.5 Mbps are already optimal)

// ─── Types ────────────────────────────────────────────────────

export interface CompressionResult {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  /** Percentage saved, e.g. 60 means 60% smaller */
  savings: number;
  skipped: boolean;
}

// ─── Public API ───────────────────────────────────────────────

/**
 * Compress a video blob by re-encoding at reduced resolution and bitrate.
 * Returns the compressed blob, or the original if compression isn't worthwhile.
 *
 * @param sourceBlob  Raw video blob
 * @param onProgress  Progress callback (0–100)
 * @param signal      AbortSignal for cancellation
 */
export async function compressVideo(
  sourceBlob: Blob,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<CompressionResult> {
  const originalSize = sourceBlob.size;

  // Skip tiny files
  if (originalSize < SKIP_THRESHOLD) {
    return makeResult(sourceBlob, true);
  }

  const blobUrl = URL.createObjectURL(sourceBlob);
  let audioCtx: AudioContext | null = null;

  try {
    throwIfAborted(signal);

    // ── 1. Load video metadata ──
    const video = document.createElement('video');
    video.playsInline = true;
    video.preload = 'auto';
    video.src = blobUrl;

    await waitFor(video, 'loadedmetadata', signal);
    // Also wait for enough data to start playback
    await waitFor(video, 'canplay', signal);

    const { videoWidth: srcW, videoHeight: srcH, duration } = video;

    // Bail on unreadable videos
    if (!srcW || !srcH || !duration || !isFinite(duration)) {
      return makeResult(sourceBlob, true);
    }

    // Already small & low-res → skip
    const maxDim = Math.max(srcW, srcH);
    if (maxDim <= MAX_DIMENSION && originalSize < SKIP_LOWRES_THRESHOLD) {
      return makeResult(sourceBlob, true);
    }

    onProgress?.(3);

    // ── 2. Calculate target dimensions (maintain aspect ratio, even values) ──
    let tgtW = srcW;
    let tgtH = srcH;
    if (maxDim > MAX_DIMENSION) {
      const scale = MAX_DIMENSION / maxDim;
      tgtW = (Math.round(srcW * scale) >> 1) << 1; // round to even
      tgtH = (Math.round(srcH * scale) >> 1) << 1;
    }

    // ── 3. Set up canvas for re-encoded frames ──
    const canvas = document.createElement('canvas');
    canvas.width = tgtW;
    canvas.height = tgtH;
    const ctx = canvas.getContext('2d')!;
    const canvasStream = canvas.captureStream(30);

    // ── 4. Capture audio track ──
    let audioTrack = captureStreamAudio(video);
    if (!audioTrack) {
      const ac = audioContextAudio(video);
      audioTrack = ac.track;
      audioCtx = ac.ctx;
    }

    // ── 5. Combine video + audio into one stream ──
    const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];
    if (audioTrack) tracks.push(audioTrack);
    const combined = new MediaStream(tracks);

    // ── 6. MediaRecorder at target bitrate ──
    const mimeType = bestMime();
    const recorder = new MediaRecorder(combined, {
      mimeType,
      videoBitsPerSecond: VIDEO_BITRATE,
    });

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.start(500);

    // ── 7. Draw loop (runs at display refresh rate) ──
    let rafId = 0;
    const draw = () => {
      if (video.paused || video.ended) return;
      ctx.drawImage(video, 0, 0, tgtW, tgtH);
      rafId = requestAnimationFrame(draw);
    };

    // ── 8. Play the video (need audio pipeline active) ──
    video.currentTime = 0;
    // For audio capture: volume=0 silences speakers, but audio pipeline stays active
    // muted=true would block audio pipeline on some browsers
    video.volume = 0;
    video.muted = false;

    try {
      await video.play();
    } catch {
      // Autoplay blocked — retry muted (audio will be lost, but video compresses)
      video.muted = true;
      await video.play();
    }

    draw();

    // ── 9. Wait for video to finish (with progress + abort handling) ──
    const progressTimer = setInterval(() => {
      if (signal?.aborted) return;
      const pct = Math.min(95, 3 + (video.currentTime / duration) * 92);
      onProgress?.(Math.round(pct));
    }, 250);

    await raceEndOrAbort(video, signal);

    clearInterval(progressTimer);
    cancelAnimationFrame(rafId);

    // ── 10. Finalize recording ──
    const compressedBlob = await finalizeRecorder(recorder, chunks, mimeType);
    combined.getTracks().forEach(t => t.stop());

    // If compression barely helped (<10% savings), return original
    if (compressedBlob.size >= originalSize * 0.9) {
      return makeResult(sourceBlob, true);
    }

    onProgress?.(100);

    const savings = Math.round((1 - compressedBlob.size / originalSize) * 100);
    return {
      blob: compressedBlob,
      originalSize,
      compressedSize: compressedBlob.size,
      savings,
      skipped: false,
    };
  } finally {
    URL.revokeObjectURL(blobUrl);
    audioCtx?.close().catch(() => {});
  }
}

/**
 * Format bytes into a human-readable string (e.g. "12.4 MB").
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Internal helpers ─────────────────────────────────────────

function makeResult(blob: Blob, skipped: boolean): CompressionResult {
  return { blob, originalSize: blob.size, compressedSize: blob.size, savings: 0, skipped };
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}

/** Wait for an event on an element, with abort + error handling. */
function waitFor(el: HTMLVideoElement, event: string, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (action: () => void) => {
      el.removeEventListener(event, onOk);
      el.removeEventListener('error', onErr);
      signal?.removeEventListener('abort', onAbort);
      action();
    };
    const onOk = () => done(resolve);
    const onErr = () => done(() => reject(new Error(`Video ${event} failed`)));
    const onAbort = () => done(() => reject(new DOMException('Aborted', 'AbortError')));

    if (signal?.aborted) { onAbort(); return; }
    el.addEventListener(event, onOk, { once: true });
    el.addEventListener('error', onErr, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Try HTMLMediaElement.captureStream() (Chrome, Firefox). */
function captureStreamAudio(video: HTMLVideoElement): MediaStreamTrack | null {
  try {
    const stream: MediaStream | undefined =
      (video as any).captureStream?.() ?? (video as any).mozCaptureStream?.();
    const tracks = stream?.getAudioTracks();
    return tracks?.[0] ?? null;
  } catch {
    return null;
  }
}

/** Fallback: AudioContext.createMediaElementSource (Safari). */
function audioContextAudio(
  video: HTMLVideoElement,
): { track: MediaStreamTrack | null; ctx: AudioContext | null } {
  try {
    const ctx = new AudioContext();
    const src = ctx.createMediaElementSource(video);
    const dest = ctx.createMediaStreamDestination();
    src.connect(dest);
    const track = dest.stream.getAudioTracks()[0] ?? null;
    if (!track) { ctx.close().catch(() => {}); return { track: null, ctx: null }; }
    return { track, ctx };
  } catch {
    return { track: null, ctx: null };
  }
}

/** Pick the best available MediaRecorder MIME type. */
function bestMime(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';
}

/** Wait for video.ended or signal.abort. */
function raceEndOrAbort(video: HTMLVideoElement, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener('ended', onEnd);
      signal?.removeEventListener('abort', onAbort);
    };
    const onEnd = () => { cleanup(); resolve(); };
    const onAbort = () => { cleanup(); video.pause(); reject(new DOMException('Aborted', 'AbortError')); };

    if (signal?.aborted) { onAbort(); return; }
    video.addEventListener('ended', onEnd, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Stop the MediaRecorder and return the final blob. */
function finalizeRecorder(
  recorder: MediaRecorder,
  chunks: Blob[],
  mimeType: string,
): Promise<Blob> {
  return new Promise((resolve) => {
    if (recorder.state === 'inactive') {
      resolve(new Blob(chunks, { type: mimeType }));
      return;
    }
    recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));
    recorder.stop();
  });
}
